import { storyblokEditable } from "@storyblok/react/rsc";

import { Headline } from "@/components/typography/Headline";
import { parseTableCell } from "@/lib/storyblok/tableCell";
import type { TableStoryblok } from "@/component-types-sb";

type TableProps = {
  blok: TableStoryblok;
};

/**
 * Cells are plain strings in Storyblok, so "- " lines become a real list and
 * other multi-line cells keep their line breaks.
 */
const TableCell = ({ value }: { value: unknown }) => {
  const parsed = parseTableCell(value);

  if (parsed.type === "list") {
    return (
      <ul className="list-disc pl-5 text-left [&>li]:mb-1 [&>li:last-child]:mb-0">
        {parsed.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }

  return (
    <>
      {parsed.lines.map((line, i) => (
        <span key={i} className="block">
          {line}
        </span>
      ))}
    </>
  );
};

const Table = ({ blok }: TableProps) => (
  <div className="pb-2" {...storyblokEditable(blok)}>
    {blok.headline && (
      <Headline as="h3" size="tall" className="mb-4">
        {blok.headline}
      </Headline>
    )}

    <div className="relative overflow-x pb-2">
      <table className="w-full text-center text-black">
        <thead className="bg-white uppercase">
          <tr>
            {blok.table?.thead.map((th: any, trIndex: number) => (
              <th scope="col" className="p-3" key={trIndex}>
                <TableCell value={th.value} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {blok.table?.tbody.map((tr: any, trIndex: number) => (
            <tr className="border-b border-gray-50" key={trIndex}>
              {tr.body.map((td: any, tdIndex: number) => (
                <td
                  className={`bg-gray-200 px-3 py-2 ${
                    parseTableCell(td.value).type === "list" ? "text-left align-top" : ""
                  }`}
                  key={tdIndex}
                >
                  <TableCell value={td.value} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default Table;
